import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/auth";
import { dashPath } from "../lib/utils-trel";
import Logo from "./Logo";
import LanguageSelector from "./LanguageSelector";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "./ui/dropdown-menu";
import { Menu, X, User as UserIcon, LayoutDashboard, Settings, LogOut, ChevronDown, ShieldCheck } from "lucide-react";

/**
 * Navbar
 * Breakpoints:
 *  - ≥1280px (xl): full horizontal nav with all 6 links + lang + auth controls.
 *  - <1280px       (covers iPad portrait + most iPad landscape): hamburger drawer.
 *  - Logo always shows. Get Started CTA stays visible at all sizes.
 * Why 1280: 6 long labels (incl. "Become a Provider" / "Become a Host") + lang
 * selector + login + CTA only fit cleanly above ~1240px. Forcing the drawer at
 * 1280 prevents the wrap glitch on iPad Air (820/1180px) and 11" iPad Pro (1194px).
 */
export default function Navbar() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const dashHref = user ? dashPath(user.role) : "/login";
  const close = () => setOpen(false);

  // Lock body scroll while the drawer is open. Drawer closes via explicit
  // onClick handlers on every NavLink + the backdrop, so a path-watcher
  // effect isn't needed.
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  // Phase 50.1 — Top nav per user spec (no shortening):
  //   How It Works · Become a Concierge · Become a Host · About
  //   right: Login · Get Started
  const navLinks = [
    { to: "/how",             testid: "nav-how",      label: t("nav.how") },
    { to: "/become-provider", testid: "nav-provider", label: t("nav.become_provider") },
    { to: "/become-host",     testid: "nav-host",     label: t("nav.become_host") },
    { to: "/about",           testid: "nav-about",    label: t("nav.about") },
  ];

  return (
    <header className="sticky top-0 z-40 glass-light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-[68px] sm:h-[72px] flex items-center justify-between gap-3">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center shrink-0"
          data-testid="nav-logo"
          onClick={close}
        >
          <Logo size="sm" tagline />
        </Link>

        {/* Desktop nav — only shows at xl+ to prevent iPad wrapping */}
        <nav
          className="hidden xl:flex items-center gap-6 text-sm flex-1 justify-center min-w-0"
          data-testid="nav-desktop-links"
        >
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={`whitespace-nowrap transition-colors ${
                l.accent === "ocean"
                  ? "hover:text-[#00AEEF] font-medium"
                  : l.muted
                  ? "text-[#071A2D]/55 hover:text-[#071A2D]"
                  : "hover:text-[#064E3B]"
              }`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        {/* Right controls */}
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {/* Language selector only on xl+ */}
          <div className="hidden xl:block">
            <LanguageSelector />
          </div>

          {user ? (
            <>
              {/* Compact user menu — collapses Dashboard/Account/Logout into a single
                  dropdown so 3 extra text links don't crush the nav at 1280–1500px. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    data-testid="nav-user-menu"
                    className="hidden xl:inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#071A2D]/10 hover:bg-[#FFF8EE] transition-colors max-w-[200px]"
                    aria-label="User menu"
                  >
                    <span className="w-7 h-7 rounded-full bg-[#064E3B] text-[#FFF8EE] flex items-center justify-center text-xs font-semibold shrink-0">
                      {(user?.name || user?.email || "?").trim().charAt(0).toUpperCase()}
                    </span>
                    <span className="text-sm font-medium text-[#071A2D] truncate max-w-[110px]">
                      {(user?.name || user?.email || "Account").split(" ")[0]}
                    </span>
                    <ChevronDown size={14} className="text-[#071A2D]/55 shrink-0" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56" data-testid="nav-user-menu-content">
                  <DropdownMenuLabel className="font-normal">
                    <div className="text-[10px] uppercase tracking-wider text-[#071A2D]/55">Signed in as</div>
                    <div className="text-sm font-medium text-[#071A2D] truncate">{user?.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to={dashHref} data-testid="nav-menu-dashboard" className="cursor-pointer">
                      <LayoutDashboard size={14} className="mr-2 text-[#064E3B]" /> {t("nav.dashboard")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/account" data-testid="nav-menu-account" className="cursor-pointer">
                      <Settings size={14} className="mr-2 text-[#064E3B]" /> {t("nav.account")}
                    </Link>
                  </DropdownMenuItem>
                  {user?.role === "customer" && (
                    <DropdownMenuItem asChild>
                      <Link to="/safety" data-testid="nav-menu-safety" className="cursor-pointer">
                        <ShieldCheck size={14} className="mr-2 text-[#00AEEF]" /> Safety Center
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={async () => { await logout(); nav("/"); }}
                    data-testid="nav-menu-logout"
                    className="cursor-pointer text-[#FF7A45] focus:text-[#FF7A45]"
                  >
                    <LogOut size={14} className="mr-2" /> {t("nav.logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <Link
              to="/login"
              data-testid="nav-login"
              className="hidden xl:inline text-sm whitespace-nowrap hover:text-[#064E3B]"
            >
              {t("nav.login")}
            </Link>
          )}

          {/* Get Started CTA — always visible, scales gracefully */}
          {!user && (
            <Link
              to="/signup"
              data-testid="nav-signup"
              className="btn-coral text-sm whitespace-nowrap px-4 py-2 sm:px-5 sm:py-2.5"
            >
              {t("nav.get_started")}
            </Link>
          )}

          {/* Hamburger — below xl */}
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((o) => !o)}
            className="xl:hidden ml-0.5 w-10 h-10 rounded-full bg-[#FFF8EE] border border-[#071A2D]/10 flex items-center justify-center hover:bg-white transition-colors"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Drawer — covers everything below xl (incl. iPad portrait & most landscape) */}
      {open && (
        <>
          {/* Backdrop */}
          <div
            className="xl:hidden fixed inset-0 top-[68px] sm:top-[72px] bg-[#071A2D]/30 backdrop-blur-sm z-30"
            onClick={close}
            aria-hidden="true"
          />
          {/* Panel */}
          <div
            className="xl:hidden absolute left-0 right-0 top-full border-t border-[#071A2D]/10 bg-[#FFF8EE] shadow-luxury-lg z-40"
            data-testid="nav-mobile-menu"
          >
            <nav className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6 flex flex-col gap-1">
              {navLinks.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={close}
                  data-testid={`mobile-${l.testid}`}
                  className={({ isActive }) =>
                    `py-3 px-3 rounded-xl text-base whitespace-nowrap transition-colors ${
                      isActive
                        ? "bg-[#064E3B]/8 text-[#064E3B] font-medium"
                        : "hover:bg-white text-[#071A2D]"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}

              <div className="my-2 border-t border-[#071A2D]/10" />

              {user ? (
                <>
                  <NavLink
                    to={dashHref}
                    onClick={close}
                    className="py-3 px-3 rounded-xl text-base hover:bg-white text-[#071A2D]"
                    data-testid="mobile-nav-dashboard"
                  >
                    {t("nav.dashboard")}
                  </NavLink>
                  <NavLink
                    to="/account"
                    onClick={close}
                    className="py-3 px-3 rounded-xl text-base hover:bg-white text-[#071A2D]"
                    data-testid="mobile-nav-account"
                  >
                    {t("nav.account")}
                  </NavLink>
                  {user?.role === "customer" && (
                    <NavLink
                      to="/safety"
                      onClick={close}
                      className="py-3 px-3 rounded-xl text-base hover:bg-white text-[#071A2D]"
                      data-testid="mobile-nav-safety"
                    >
                      Safety Center
                    </NavLink>
                  )}
                  <button
                    onClick={async () => { close(); await logout(); nav("/"); }}
                    data-testid="mobile-nav-logout"
                    className="mt-2 btn-outline-emerald text-sm"
                  >
                    {t("nav.logout")}
                  </button>
                </>
              ) : (
                <NavLink
                  to="/login"
                  onClick={close}
                  className="py-3 px-3 rounded-xl text-base hover:bg-white text-[#071A2D]"
                  data-testid="mobile-nav-login"
                >
                  {t("nav.login")}
                </NavLink>
              )}

              <div className="mt-3">
                <LanguageSelector variant="mobile" />
              </div>
            </nav>
          </div>
        </>
      )}
    </header>
  );
}
